import { useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import { ErrorScreen } from '@components/error-screen';
import {
  IntegrationEndpointMinimal,
  IntegrationWebhookMinimal,
} from '@components/integrations/integrations-types';
import { useRotatingLoadingMessages } from '@components/rotating-loading-messages/use-rotating-loading-messages';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';

import { useAIAgents } from 'api/ai-agents/hooks';
import { AIAgent } from 'api/ai-agents/requests';
import {
  useJourneyIntegrationWebhook,
  useTravellerJourneyIntegrationEndpoint,
} from 'api/integrations/hooks';
import { useJourneyActionForms } from 'api/journey-action-forms/hooks';
import { useTravellerJourneyActions } from 'api/traveller-actions/hooks';
import { TravellerJourneyDetails } from 'api/traveller-journeys/types';
import { TravellerJourneyAction } from 'api/types';
import { getShortIdToDescriptiveMap, TestIdContext } from 'ui/authenticated/main/helpers';
import { journeyActionFormContextMapType, JourneyContext } from 'ui/journey-builder/base-builder';
import { useNodes } from 'ui/traveller-journey-builder/hooks/use-nodes';
import { nodeTypes } from 'ui/traveller-journey-builder/node-types';
import { TravellerJourneyNode } from 'ui/traveller-journey-builder/node-types/types';
import { TravellerJourneyBuilder } from 'ui/traveller-journey-builder/traveller-journey-builder';

const useLoadActionsAndSetNodes = (traveller: TravellerJourneyDetails) => {
  const { t } = useScopedTranslate(locale);
  const { setNodes } = useNodes();
  const actionsQuery = useTravellerJourneyActions(traveller.shortId);
  const endpointQuery = useTravellerJourneyIntegrationEndpoint(traveller.shortId);
  const webhookQuery = useJourneyIntegrationWebhook(traveller.journey.shortId);
  const agentsQuery = useAIAgents();

  const [hasLoaded, setHasLoaded] = useState(false);
  useRotatingLoadingMessages({ isEnabled: !hasLoaded, title: t('loading') });

  const allDone =
    actionsQuery.isSuccess &&
    endpointQuery.isSuccess &&
    webhookQuery.isSuccess &&
    agentsQuery.isSuccess;

  const hasErrors =
    actionsQuery.isError || endpointQuery.isError || webhookQuery.isError || agentsQuery.isError;

  useEffect(() => {
    if (!allDone) return;

    setHasLoaded(true);
    setNodes(
      convertToNodes({
        actions: actionsQuery.data,
        integrations: endpointQuery.data,
        traveller,
        webhooks: webhookQuery.data,
        aiAgents: agentsQuery.data,
        travellerJourneyId: traveller.shortId,
      })
    );
  }, [
    actionsQuery.data,
    agentsQuery.data,
    allDone,
    endpointQuery.data,
    setNodes,
    traveller,
    webhookQuery.data,
    setHasLoaded,
  ]);

  return {
    actions: actionsQuery.data ?? [],
    hasErrors,
  };
};

export const TravellerJourney = ({ traveller }: { traveller: TravellerJourneyDetails }) => {
  const { actions, hasErrors } = useLoadActionsAndSetNodes(traveller);
  const { t } = useScopedTranslate(locale);

  const journeyActionFormsQuery = useJourneyActionForms({
    includePublished: true,
    journeyId: traveller.journey.shortId,
    travellerJourneyId: traveller.shortId,
  });

  const journeyActionForms = (journeyActionFormsQuery.data ?? []).reduce((acc, form) => {
    if (form.journeyActionId) {
      acc[form.journeyActionId] = {
        shortId: form.shortId,
        name: form.name,
        formUrl: form.formUrl ?? undefined,
      };
    }
    return acc;
  }, {} as journeyActionFormContextMapType);

  const shortIdToDescriptiveMap = useMemo(() => {
    return getShortIdToDescriptiveMap(actions);
  }, [actions]);

  return (
    <JourneyContext.Provider
      value={{
        journeyActionForms,
        journeyForms: [],
        webhooks: [],
        pollingConfigs: [],
        triggersCount: 0,
        journeyId: traveller.journey.shortId,
      }}
    >
      <TestIdContext.Provider value={shortIdToDescriptiveMap}>
        <Container>
          {hasErrors ? (
            <ErrorScreen
              code={500}
              heading={t('actionsFailedHeading')}
              message={t('actionsFailedMessage')}
            />
          ) : (
            <TravellerJourneyBuilder actions={actions} traveller={traveller} />
          )}
        </Container>
      </TestIdContext.Provider>
    </JourneyContext.Provider>
  );
};

const Container = styled.div({
  flexGrow: 1,
  position: 'relative',
});

const getDescription = (action: TravellerJourneyAction, availableValues: Record<string, any>) => {
  const { t } = getScopedTranslate(locale);
  if (action.description) {
    return action.description;
  } else {
    const node = nodeTypes[action.action];
    return (
      (node &&
        'getDescription' in node &&
        typeof node.getDescription === 'function' &&
        node.getDescription(action, availableValues)) ||
      t('noDescription')
    );
  }
};

const convertToNodes = ({
  actions,
  integrations,
  traveller,
  webhooks,
  aiAgents,
  travellerJourneyId,
}: {
  actions: TravellerJourneyAction[];
  integrations: IntegrationEndpointMinimal[];
  traveller: TravellerJourneyDetails;
  webhooks: IntegrationWebhookMinimal[];
  aiAgents: AIAgent[];
  travellerJourneyId: string;
}): TravellerJourneyNode[] => {
  const hasDescendants = (shortId: string) => {
    return actions.filter((node) => node.parentAction === shortId).length > 0;
  };

  const parentCompleted = (shortId?: string) => {
    if (!shortId) return true;
    return (
      actions.filter((node) => node.shortId === shortId && !!node.result?.dateSucceeded).length > 0
    );
  };

  const parentSkipped = (shortId?: string) => {
    if (!shortId) return true;
    return actions.filter((node) => node.shortId === shortId && node.isSkipped).length > 0;
  };

  const integrationEndpoint = (action: TravellerJourneyAction<'integration'>) => {
    const endpointId = action.values?.integrationEndpointId?.value ?? '';
    return integrations.find((integration) => integration.shortId === endpointId);
  };

  const integrationWebhook = (action: TravellerJourneyAction<'webhook'>) => {
    const webhookId = action.values?.integrationWebhookId?.value ?? '';
    return webhooks.find((integration) => integration.shortId === webhookId);
  };

  const getAiAgent = (action: TravellerJourneyAction<'ask_ai_agent'>) => {
    const agentId = action.values?.aiAgentId ?? '';
    return aiAgents.find((agent) => agent.shortId === agentId);
  };

  const commonProps = {
    isJourneyPaused: traveller.isPaused,
    travellerJourneyId: traveller.shortId,
    journeyId: traveller.journey.shortId,
  };

  const nodes = [] as TravellerJourneyNode[];

  if (actions.length > 0) {
    nodes.push({
      id: 'journey-triggers',
      type: 'journey_triggers',
      parentId: undefined,
      data: {
        travellerJourneyId,
        isConfigured: true,
        nodeDescription: 'Journey Triggers',
        dependants: [],
        isJourneyPaused: traveller.isPaused,
        journeyId: traveller.journey.shortId,
        groupActions: [],
        values: {},
        data: {},
      },
    });
  }

  actions.forEach((item) => {
    const integration =
      item.action === 'integration'
        ? integrationEndpoint(item as TravellerJourneyAction<'integration'>)
        : item.action === 'webhook'
        ? integrationWebhook(item as TravellerJourneyAction<'webhook'>)
        : undefined;

    const aiAgent =
      item.action === 'ask_ai_agent'
        ? getAiAgent(item as TravellerJourneyAction<'ask_ai_agent'>)
        : undefined;

    nodes.push({
      id: item.shortId,
      type: item.action,
      parentId: item.action === 'journey_start' ? 'journey-triggers' : item.parentAction,
      data: {
        ...item,
        ...commonProps,
        values: item.values ?? {},
        data: item.data ?? {},
        nodeDescription: getDescription(item, traveller.availableValues),
        status: item.status,
        assignees: item.assignees,
        isCompleted: Boolean(
          item.result?.dateSucceeded ||
            ((item.action === 'condition' || item.action === 'condition_v2') &&
              item.isHalted &&
              item.result?.dateStarted)
        ),
        isInProgress: Boolean(item.result?.dateStarted && !item.result?.dateSucceeded),
        isError: Boolean(
          item.result?.dateStarted && !item.result?.dateSucceeded && item.result?.dateFailed
        ),
        parentCompleted: parentCompleted(item.parentAction),
        parentSkipped: parentSkipped(item.parentAction),
        hasDescendants: hasDescendants(item.shortId),
        integration,
        aiAgent,
        groupActions: item.groupActions,
        result: item.result,
      },
    });
  });

  return nodes;
};

const locale = {
  en: {
    loading: 'Loading...',
    noDescription: 'No description',
    actionsFailedHeading: 'Loading traveller failed',
    actionsFailedMessage: 'Our team has been notified',
  },
};
